import type { Tier } from './tiers';

export type Region = {
  id: string;
  name: string;
  /** ISO alpha-2 codes of member states */
  memberCountries: string[];
  /** Key in ANALYSIS_REGISTRY (see countries.ts). Optional — only set when a region-level analysis exists. */
  analysisKey?: string;
  /** If analysisKey is set, this tier colors all member countries that don't have their own analysis. */
  tier?: Tier;
  lastUpdated?: string;
  summary?: string;
};

export const REGIONS: Record<string, Region> = {
  EU: {
    id: 'EU',
    name: 'European Union',
    memberCountries: [
      'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT',
      'LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
    ],
    analysisKey: 'EuropeanUnion',
    lastUpdated: '2026-04-16',
    summary:
      '27 EU member states + UK. Unified VAT/OSS + Stripe Connect + DAC7 out of scope (intangible tickets). Netherlands BV node recommended; Ireland remains OSS MSI.',
  },
  EEA: {
    id: 'EEA',
    name: 'European Economic Area',
    memberCountries: [
      'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT',
      'LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','LI','NO',
    ],
  },
  UK_IRE: {
    id: 'UK_IRE',
    name: 'UK & Ireland',
    memberCountries: ['GB', 'IE'],
  },
  NORDICS: {
    id: 'NORDICS',
    name: 'Nordics',
    memberCountries: ['DK', 'FI', 'IS', 'NO', 'SE'],
  },
  LATAM: {
    id: 'LATAM',
    name: 'Latin America',
    memberCountries: [
      'AR','BO','BR','CL','CO','CR','CU','DO','EC','SV','GT','HN','MX','NI','PA','PY','PE','UY','VE',
    ],
  },
  MENA: {
    id: 'MENA',
    name: 'Middle East & North Africa',
    memberCountries: [
      'DZ','BH','EG','IR','IQ','IL','JO','KW','LB','LY','MA','OM','QA','SA','SY','TN','AE','YE',
    ],
  },
  GCC: {
    id: 'GCC',
    name: 'Gulf Cooperation Council',
    memberCountries: ['BH', 'KW', 'OM', 'QA', 'SA', 'AE'],
  },
  ASEAN: {
    id: 'ASEAN',
    name: 'ASEAN',
    memberCountries: ['BN', 'KH', 'ID', 'LA', 'MY', 'MM', 'PH', 'SG', 'TH', 'VN'],
  },
  APAC: {
    id: 'APAC',
    name: 'Asia-Pacific',
    memberCountries: [
      'AU','NZ','JP','KR','CN','TW','HK','SG','MY','TH','ID','PH','VN','IN',
    ],
  },
  SSA: {
    id: 'SSA',
    name: 'Sub-Saharan Africa',
    memberCountries: [
      'AO','BJ','BW','BF','BI','CV','CM','CF','TD','KM','CG','CD','CI','DJ','GQ','ER','SZ','ET',
      'GA','GM','GH','GN','GW','KE','LS','LR','MG','MW','ML','MR','MU','MZ','NA','NE','NG','RW',
      'ST','SN','SC','SL','SO','ZA','SS','TZ','TG','UG','ZM','ZW',
    ],
  },
  NA: {
    id: 'NA',
    name: 'North America',
    memberCountries: ['US', 'CA', 'MX'],
  },
};

export function getRegionsForCountry(iso2: string): Region[] {
  return Object.values(REGIONS).filter((r) => r.memberCountries.includes(iso2));
}
